<template>
  <div class="grid-collection">
    <h3>{{ title }}</h3>
    <div class="grid-deals">
      <div
        v-for="deal in deals.slice(0, 3)"
        :id="deal.id"
        :key="deal.id"
        class="grid-deal"
      >
        <div class="grid-deal-container">
          <a :href="`${deal.get_absolute_url}`">
            <p>
              {{ deal.card_description }}
            </p>
          </a>
          <div class="grid-deal-summary">
            <a :href="`${deal.get_absolute_url}`">
              <img
                :src="`${deal.media_url}?width=128&height=84&aspect_ratio=3:2`"
                :alt="deal.public_name"
                width="64"
                height="42"
              >
            </a>
            <div class="grid-deal-details">
              <a :href="`${deal.get_absolute_url}`">{{ deal.public_name }}</a>
              <div class="grid-deal-meta">
                <DealPrice
                  class="grid-deal-price"
                  :price="deal.price"
                  :original-price="deal.original_price"
                  :is-free="deal.is_free"
                />
                <DealRating
                  class="grid-deal-rating"
                  is-deal-card
                  condensed
                  :deal="deal"
                />
              </div>
            </div>
          </div>
        </div>
        <DealAttribute class="grid-deal-attribute" :attr="dealAttribute(deal)">
          <div
            v-if="attr"
            slot-scope="{ attr }"
            class="grid-deal-attribute"
          >
            {{ attr.name }}
            <a
              :href="`/collections/${attr.slug}/${attr.value.slug}/`"
            >
              {{ attr.value.name }}
            </a>
          </div>
        </DealAttribute>
      </div>
    </div>
  </div>
</template>

<script>
import { DEAL_PLACEHOLDER_IMG } from '@/constants/global';
import DealRating from '../utilities/DealRating.vue';
import DealPrice from '~/components/common/DealPrice.vue';

const DealAttribute = {
  render() {
    return this.$scopedSlots.default(this.$attrs);
  },
};

export default {
  components: {
    DealRating,
    DealAttribute,
    DealPrice,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    deals: {
      type: Array,
      required: true,
    },
  },
  computed: {
    attribute() {
      return 'best_for';
    },
    lazyPlaceholderImage() {
      return DEAL_PLACEHOLDER_IMG;
    },
  },
  methods: {
    dealAttribute(deal) {
      const { attributes } = this.$store.state.browse;

      if (deal.attributes[this.attribute]) {
        const attr = attributes.find((a) => a.slug === this.attribute);

        if (attr) {
          return {
            name: attr.name,
            slug: this.attribute.replace('_', '-'),
            value: {
              slug: deal.attributes[this.attribute][0].toLowerCase()
                .trim()
                .replace(/[^\w\s-]/g, '')
                .replace(/[\s_-]+/g, '-')
                .replace(/^-+|-+$/g, ''),
              name: deal.attributes[this.attribute][0],
            },
          };
        }
      }

      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~/styles/utils/breakpoints';

.grid-collection {
  margin-bottom: 24px;

  h3 {
    font-family: Barlow, sans-serif;
    font-weight: 700;
    font-size: 24px;
    line-height: 32px;
    color: #07c03b;
  }
}

.grid-deal-details {
  display: flex;
  flex-direction: column;

  a {
    font-weight: 700;
    font-size: 14px;
    line-height: 21px;
    color: #0e6ef4;
    display: block;
    flex-grow: 0;
  }

  .grid-deal-meta {
    display: grid;
    flex-grow: 1;
    grid-template-columns: max-content max-content;

    .grid-deal-price {
      width: auto !important;
      padding-right: 8px;
      margin-right: 8px;
      border-right: 1px solid #8e9baa;
    }

    .grid-deal-price ::v-deep(p) {
      margin-bottom: 0 !important;
      line-height: 24px !important;
    }

    .grid-deal-rating ::v-deep(.appsumo-taco-rating-container) {
      margin: 4px 0;
      min-height: 0;
    }
  }
}

.grid-deals {
  display: grid;
  grid-gap: 24px;
  grid-template-columns: repeat(3, 1fr);

  .grid-deal {
    border: 1px solid #dadde5;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    font-family: Roboto, sans-serif;

    .grid-deal-container {
      flex-grow: 1;
      padding: 16px;
      display: flex;
      flex-direction: column;

      p {
        flex-grow: 1;
        font-size: 16px;
        line-height: 24px;
        display: -webkit-box; /* stylelint-disable-line value-no-vendor-prefix */
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical; /* stylelint-disable-line */
        overflow: hidden;
        text-overflow: ellipsis;
        color: #021414;
      }

      > a {
        flex-grow: 1;
      }

      > a:hover {
        text-decoration: none;
      }
    }

    .grid-deal-attribute {
      background: #f5f5f9;
      flex-grow: 0;
      padding: 8px 16px;
      font-size: 14px;
    }
  }
}

.grid-deal-summary {
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 64px 1fr;

  img {
    width: 64px;
    border-radius: 4px;
  }
}

@include media-breakpoint-down(sm) {
  .grid-deals {
    display: flex;
    flex-direction: column;

    .grid-deal:nth-child(3) {
      display: none;
    }
  }
}

@include media-breakpoint-down(md) {
  .grid-deals {
    grid-template-columns: repeat(2, 1fr);

    .grid-deal:nth-child(3) {
      display: none;
    }
  }
}
</style>
